import React, { useEffect } from 'react';
import { Card, Select, Input, Button, Progress } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import ModernCVUpload from './ModernCVUpload';
import './ModernAnalysisStep.css';

// Helper function to get cookie value
const getCookie = (name) => {
    var match = document.cookie.match(RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? match[1] : null;
};

// Encapsulates the priority selectors
const SelectPriority = ({ dropdownOptions, selectedOptions, setSelectedOptions, isBackButtonClicked }) => {
    const handleSelectChange = (value, priority) => {
        setSelectedOptions(prev => ({ ...prev, [priority]: value }));
    };

    return (
        <div className="priority-container">
            {/* 1st Priority */}
            <div className="priority-item">
                <div className="priority-label">1st Priority</div>
                <Select
                    value={selectedOptions.firstPriority}
                    onChange={value => handleSelectChange(value, 'firstPriority')}
                    className="priority-select"
                    placeholder="Select"
                    disabled={selectedOptions.chosenJD !== "" || isBackButtonClicked}
                >
                    {dropdownOptions
                        .filter(option => option === 'Default' || !Object.values(selectedOptions).includes(option))
                        .map(option => (
                            <Select.Option key={option} value={option}>
                                {option}
                            </Select.Option>
                        ))}
                </Select>
            </div>

            {/* 2nd Priority */}
            <div className="priority-item">
                <div className="priority-label">2nd Priority</div>
                <Select
                    value={selectedOptions.secondPriority}
                    onChange={value => handleSelectChange(value, 'secondPriority')}
                    className="priority-select"
                    placeholder="Select"
                    disabled={selectedOptions.chosenJD !== "" || isBackButtonClicked}
                >
                    {dropdownOptions
                        .filter(option => option === 'Default' || !Object.values(selectedOptions).includes(option))
                        .map(option => (
                            <Select.Option key={option} value={option}>
                                {option}
                            </Select.Option>
                        ))}
                </Select>
            </div>

            {/* 3rd Priority */}
            <div className="priority-item">
                <div className="priority-label">3rd Priority</div>
                <Select
                    value={selectedOptions.thirdPriority}
                    onChange={value => handleSelectChange(value, 'thirdPriority')}
                    className="priority-select"
                    placeholder="Select"
                    disabled={selectedOptions.chosenJD !== "" || isBackButtonClicked}
                >
                    {dropdownOptions
                        .filter(option => option === 'Default' || !Object.values(selectedOptions).includes(option))
                        .map(option => (
                            <Select.Option key={option} value={option}>
                                {option}
                            </Select.Option>
                        ))}
                </Select>
            </div>
        </div>
    );
};

// Main component for step 2
const ModernAnalysisStep = ({
                                dropdownOptions,
                                selectedOptions,
                                setSelectedOptions,
                                additionalCriteria,
                                handleCriteriaChange,
                                setAdditionalCriteria,
                                AdditionalContent,
                                setAdditionalContent,
                                handleCvFilesAdded,
                                handleDeleteCvFile,
                                handleUpload,
                                isMainUploadLoading,
                                loading,
                                progress,
                                showCheck,
                                isBackButtonClicked,
                                backendURL
                            }) => {

    // Fetch job settings when an existing job is selected
    useEffect(() => {
        const fetchJobSettings = async () => {
            if (selectedOptions.chosenJD && selectedOptions.chosenJD !== "") {
                try {
                    const sessionID = getCookie("userSession");
                    const response = await fetch(
                        `${backendURL}/v1/job-settings?sessionID=${sessionID}&jobID=${selectedOptions.chosenJD}`
                    );

                    if (response.ok) {
                        const data = await response.json();

                        // Update priorities
                        setSelectedOptions(prev => ({
                            ...prev,
                            firstPriority: data.priorities.firstPriority,
                            secondPriority: data.priorities.secondPriority,
                            thirdPriority: data.priorities.thirdPriority,
                            AdditionalContentImportance: data.priorities.additionalContentImportance,
                        }));

                        // Update criteria
                        setAdditionalCriteria({
                            criteria1: data.criteria[0] || "",
                            criteria2: data.criteria[1] || "",
                            criteria3: data.criteria[2] || "",
                        });

                        // Update additional content
                        setAdditionalContent(data.additionalContent || "");
                    }
                } catch (error) {
                    console.error("Error fetching job settings:", error);
                }
            }
        };

        fetchJobSettings();
    }, [selectedOptions.chosenJD]);

    return (
        <div className="modern-analysis-container">
            <div className="modern-analysis-background">
                <div className="modern-analysis-content">
                    <Card className="modern-analysis-card">
                        <div className="modern-section-header">
                            <h2 className="modern-title">Prioritise Analysis Results</h2>
                            <p className="modern-subtitle">Select a category you want to prioritise your results by.</p>
                        </div>

                        <SelectPriority
                            dropdownOptions={dropdownOptions}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions}
                            isBackButtonClicked={isBackButtonClicked}
                        />

                        {/* Additional Skills */}
                        <div className="modern-section">
                            <h2 className="modern-section-title">Additional Skills (Optional)</h2>
                            <p className="modern-section-description">
                                Specify additional skills you want to check if the candidate posseses (on a yes/no basis).
                            </p>
                            <p className="modern-section-example">For instance: 'SQL', 'First Aid', 'Excel'</p>

                            <div className="additional-skills-container">
                                <Input
                                    value={additionalCriteria.criteria1}
                                    onChange={handleCriteriaChange('criteria1')}
                                    placeholder="Additional Criteria 1"
                                    className="modern-input"
                                    disabled={selectedOptions.chosenJD !== '' || isBackButtonClicked}
                                    maxLength={100}
                                />
                                <Input
                                    value={additionalCriteria.criteria2}
                                    onChange={handleCriteriaChange('criteria2')}
                                    placeholder="Additional Criteria 2"
                                    className="modern-input"
                                    disabled={selectedOptions.chosenJD !== '' || isBackButtonClicked}
                                    maxLength={100}
                                />
                                <Input
                                    value={additionalCriteria.criteria3}
                                    onChange={handleCriteriaChange('criteria3')}
                                    placeholder="Additional Criteria 3"
                                    className="modern-input"
                                    disabled={selectedOptions.chosenJD !== '' || isBackButtonClicked}
                                    maxLength={100}
                                />
                            </div>
                        </div>

                        {/* Additional Content */}
                        <div className="modern-section">
                            <h2 className="modern-section-title">Additional Content</h2>
                            <p className="modern-section-description">
                                Provide any additional information about the job description and select its influence level.
                            </p>

                            <div className="influence-level-container">
                                <div className="influence-level-header">
                                    <div className="influence-level-label">Influence Level</div>
                                    <span className="influence-level-description">
                                        {selectedOptions.AdditionalContentImportance === 'low' && 'No extra influence on analysis'}
                                        {selectedOptions.AdditionalContentImportance === 'medium' && 'Some extra influence on analysis'}
                                        {selectedOptions.AdditionalContentImportance === 'high' && 'Very strong influence on analysis'}
                                    </span>
                                </div>

                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={
                                        selectedOptions.AdditionalContentImportance === 'low'
                                            ? 0
                                            : selectedOptions.AdditionalContentImportance === 'medium'
                                                ? 50
                                                : 100
                                    }
                                    onChange={e => {
                                        const value = parseInt(e.target.value, 10);
                                        const newValue = value < 33 ? 'low' : value < 66 ? 'medium' : 'high';
                                        setSelectedOptions(prev => ({
                                            ...prev,
                                            AdditionalContentImportance: newValue
                                        }));
                                    }}
                                    disabled={selectedOptions.chosenJD !== '' || isBackButtonClicked}
                                    className="modern-slider"
                                />

                                <div className="slider-labels">
                                    <span>Low</span>
                                    <span>Medium</span>
                                    <span>High</span>
                                </div>
                            </div>

                            <Input.TextArea
                                value={AdditionalContent}
                                onChange={e => setAdditionalContent(e.target.value)}
                                placeholder="Add any additional information about the job description"
                                className="modern-textarea"
                                disabled={selectedOptions.chosenJD !== '' || isBackButtonClicked}
                                rows={4}
                                maxLength={2000}
                                spellCheck
                            />
                        </div>

                        {/* File upload and run button */}
                        <div className="upload-section">
                            <ModernCVUpload onFilesAdded={handleCvFilesAdded} onDeleteFile={handleDeleteCvFile} />
                            <div className="run-button-container">
                                <Button
                                    type="primary"
                                    onClick={handleUpload}
                                    loading={isMainUploadLoading}
                                    className="modern-run-button"
                                    size="large"
                                >
                                    {isMainUploadLoading ? 'Processing...' : 'Run analysis'}
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Progress HUD */}
            {loading && (
                <>
                    <div className="modern-progress-overlay" />
                    <div className="modern-progress-container">
                        {/* SVG gradient definition */}
                        <svg width="0" height="0" style={{ position: 'absolute' }}>
                            <defs>
                                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" style={{ stopColor: '#FA370A', stopOpacity: 1 }} />
                                    <stop offset="100%" style={{ stopColor: '#FF45C1', stopOpacity: 1 }} />
                                </linearGradient>
                            </defs>
                        </svg>

                        <Progress
                            type="circle"
                            percent={progress}
                            className="modern-progress-circle"
                            size={120}
                            strokeWidth={6}
                            format={(percent) => `${Math.round(percent)}%`}
                        />

                        <div className="modern-progress-message">
                            {progress < 20 && "Initializing analysis..."}
                            {progress >= 20 && progress < 60 && "Processing CVs with AI..."}
                            {progress >= 60 && progress < 90 && "Ranking candidates..."}
                            {progress >= 90 && "Almost complete..."}
                        </div>
                    </div>
                </>
            )}

            {/* Checkmark on completion */}
            {showCheck && (
                <>
                    <div className="modern-progress-overlay" />
                    <div className="modern-progress-container">
                        <CheckCircleOutlined className="modern-success-icon" />
                        <div className="modern-success-message">
                            Analysis Complete!
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ModernAnalysisStep;